import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { StatusPill } from "../components/UI";
import { useAuth } from "../lib/auth";

const STATUS_MAP = {
  "not-connected": { tone: "neutral", label: "NOT CONNECTED" },
  "connected": { tone: "green", label: "CONNECTED" },
  "problem": { tone: "red", label: "PROBLEM — NEEDS A LOOK" },
  "coming-soon": { tone: "blue", label: "COMING IN PHASE 2" },
};

const EVENT_LABEL = {
  "connected": "CONNECTED",
  "tested": "TESTED",
  "test-failed": "TEST FAILED",
  "switched-on": "SWITCHED ON",
  "switched-off": "SWITCHED OFF",
  "disconnected": "DISCONNECTED",
};

function formatTs(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const day = d.getDate();
  const monthShort = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][d.getMonth()];
  let hours = d.getHours();
  const mins = String(d.getMinutes()).padStart(2, "0");
  const suffix = hours >= 12 ? "pm" : "am";
  hours = hours % 12 || 12;
  return `${day} ${monthShort} ${hours}:${mins}${suffix}`;
}

function ConnectDialog({ meta, initial, onClose, onSaved }) {
  const [creds, setCreds] = useState(() => {
    const c = {};
    (meta.fields || []).forEach(f => { c[f.name] = ""; });
    return c;
  });
  const [config, setConfig] = useState(() => {
    const c = {};
    (meta.config_fields || []).forEach(f => { c[f.name] = initial?.config?.[f.name] || ""; });
    return c;
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const submit = async () => {
    setBusy(true); setErr(""); setMsg("");
    try {
      const r = await api.post(`/connections/${meta.key}/connect`, { credentials: creds, config });
      setMsg(r.data.message);
      onSaved();
      onClose();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="ach-modal-backdrop" data-testid="connect-dialog" onClick={onClose}>
      <div className="ach-modal" onClick={(e)=>e.stopPropagation()}>
        <div className="ach-modal__header">
          <div>
            <h2 style={{margin:0, fontSize:16, fontWeight:600}}>Connect {meta.name}</h2>
            <div style={{fontSize:12, color:"var(--ach-text-2)", marginTop:4}}>{meta.description}</div>
          </div>
          <button className="ach-btn ach-btn--grey" onClick={onClose} data-testid="connect-close" style={{padding:"6px 10px"}}>Close</button>
        </div>

        <div className="ach-modal__body">
          <div className="ach-form-grid" style={{gridTemplateColumns: "1fr"}}>
            {meta.fields.map(f => (
              <div key={f.name} className="ach-field">
                <label>{f.label}</label>
                <input
                  data-testid={`connect-field-${f.name}`}
                  type={f.type === "password" ? "password" : "text"}
                  value={creds[f.name] || ""}
                  onChange={(e)=>setCreds({...creds, [f.name]: e.target.value})}
                  placeholder={f.type === "password" ? "Paste from your software" : ""}
                  autoComplete="off"
                />
                <div className="helper">{f.help}</div>
              </div>
            ))}
            {meta.config_fields.map(f => (
              <div key={f.name} className="ach-field">
                <label>{f.label}</label>
                <input
                  data-testid={`connect-config-${f.name}`}
                  value={config[f.name] || ""}
                  onChange={(e)=>setConfig({...config, [f.name]: e.target.value})}
                />
                <div className="helper">{f.help}</div>
              </div>
            ))}
            {err && <div style={{color:"var(--ach-red)", background:"var(--ach-red-tint)", padding:8, borderRadius:6, fontSize:12}}>{err}</div>}
            {msg && !err && <div style={{color:"var(--ach-green)", background:"var(--ach-green-tint)", padding:8, borderRadius:6, fontSize:12}}>{msg}</div>}
          </div>
        </div>

        <div className="ach-modal__footer">
          <button className="ach-btn ach-btn--primary" onClick={submit} disabled={busy} data-testid="connect-save-test">
            {busy ? "Testing…" : "Save & test connection"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Xero special-case: platform OAuth 2.0 + PKCE button ----------
function XeroConnectButton({ onErr }) {
  const [cfg, setCfg] = useState(null); // {configured, message}
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/connections/xero/oauth-config").then(r => setCfg(r.data)).catch(() => setCfg({configured:false, message:"Couldn't check Xero config just now."}));
  }, []);

  const start = async () => {
    setBusy(true); onErr("");
    try {
      const r = await api.post("/connections/xero/oauth-start");
      // Send the browser off to Xero's consent screen. When the Director
      // approves, Xero will redirect back to /api/connections/xero/oauth-callback
      // which then bounces to /connections?xero=ok&msg=...
      window.location.assign(r.data.authorize_url);
    } catch (e) {
      onErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  if (!cfg) {
    return <button className="ach-btn ach-btn--primary" disabled data-testid="btn-connect-xero">Loading…</button>;
  }
  if (!cfg.configured) {
    return (
      <div style={{padding:10, background:"var(--ach-page-bg)", borderRadius:8, fontSize:12, color:"var(--ach-text-2)", maxWidth:520}} data-testid="xero-not-configured">
        <div style={{fontWeight:600, color:"var(--ach-charcoal)", marginBottom:4}}>Xero isn&apos;t wired up on this system yet</div>
        {cfg.message}
      </div>
    );
  }
  return (
    <button className="ach-btn ach-btn--primary" onClick={start} disabled={busy} data-testid="btn-connect-xero">
      {busy ? "Opening Xero…" : "Connect to Xero →"}
    </button>
  );
}


// ---------- Xero integration settings + contact sync (shown ONLY when connected) ----------
function XeroIntegrationSettings({ canManage }) {
  const [cfg, setCfg] = useState(null);          // full settings dict
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState("");
  const [flash, setFlash] = useState(null);      // {tone, text}
  const [syncResult, setSyncResult] = useState(null);

  useEffect(() => {
    api.get("/settings/xero-integration")
       .then(r => { setCfg(r.data); setDirty(false); })
       .catch(e => setFlash({tone:"red", text: formatApiErrorDetail(e.response?.data?.detail) || e.message}));
  }, []);

  if (!cfg) return null;

  const save = async () => {
    setBusy("save"); setFlash(null);
    try {
      const r = await api.put("/settings/xero-integration", cfg);
      // Reload the full settings dict — the PUT response has the same shape,
      // but going back to source of truth is cheap and stops the shape drifting.
      setCfg(r.data);
      setDirty(false);
      setFlash({tone:"green", text:"Saved."});
    } catch (e) {
      setFlash({tone:"red", text: formatApiErrorDetail(e.response?.data?.detail) || e.message});
    } finally { setBusy(""); }
  };

  // --- helpers for the item 3/4 toggles ---
  const setCodeFor = (chargeType, v) => {
    setCfg({ ...cfg, account_codes: { ...(cfg.account_codes || {}), [chargeType]: v }});
    setDirty(true);
  };
  const setIncludeFor = (chargeType, v) => {
    setCfg({ ...cfg, entity_totals_include: { ...(cfg.entity_totals_include || {}), [chargeType]: v }});
    setDirty(true);
  };
  // consolidate_invoices is stored as "one invoice per customer" (True = consolidate).
  // We surface it as "One invoice per machine" so Todd never reads a double negative:
  //   UI toggle ON  → per_machine_ui = true  → consolidate_invoices = false
  //   UI toggle OFF → per_machine_ui = false → consolidate_invoices = true
  // The word "consolidate" appears nowhere in the visible copy.
  const perMachineUI = !cfg.consolidate_invoices;
  const setPerMachine = (v) => {
    setCfg({ ...cfg, consolidate_invoices: !v });
    setDirty(true);
  };

  const runSync = async () => {
    setBusy("sync"); setFlash(null); setSyncResult(null);
    try {
      const r = await api.post("/connections/xero/sync-contacts");
      setSyncResult(r.data);
      setFlash({tone:"green", text: r.data.message});
    } catch (e) {
      setFlash({tone:"red", text: formatApiErrorDetail(e.response?.data?.detail) || e.message});
    } finally { setBusy(""); }
  };

  return (
    <div style={{marginTop:14, borderTop:"1px solid var(--ach-row-divider)", paddingTop:12}} data-testid="xero-integration-settings">
      <div style={{fontSize:11, textTransform:"uppercase", letterSpacing:0.6, color:"var(--ach-text-2)", marginBottom:10}}>Integration settings</div>

      <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:16}}>
        <div className="ach-field">
          <label>Xero account code for hire income</label>
          <input value={cfg.account_code}
                 onChange={e => { setCfg({...cfg, account_code: e.target.value}); setDirty(true); }}
                 disabled={!canManage || busy === "save"}
                 data-testid="xero-account-code-input"
                 placeholder="e.g. 200" />
          <div className="helper">Every invoice line posts against this Chart-of-Accounts code (default: 200 — &quot;Crane Hire&quot;). Change here if your chart is different.</div>
        </div>

        <div className="ach-field">
          <label>Sync customers with Xero contacts</label>
          <div style={{display:"flex", alignItems:"center", gap:10, marginTop:4}}>
            <label style={{display:"flex", alignItems:"center", gap:8, cursor: canManage ? "pointer" : "not-allowed"}}>
              <input type="checkbox"
                     checked={!!cfg.contact_sync_enabled}
                     onChange={e => { setCfg({...cfg, contact_sync_enabled: e.target.checked}); setDirty(true); }}
                     disabled={!canManage || busy === "save"}
                     data-testid="xero-contact-sync-toggle" />
              <span style={{fontSize:13}}>{cfg.contact_sync_enabled ? "On" : "Off"}</span>
            </label>
          </div>
          <div className="helper">When on, new/edited ACH customers are pushed to Xero automatically. Match by name first, no duplicates.</div>
        </div>
      </div>

      {/* Invoicing block — Director-only. Absent for everyone else per the
          brief: not disabled, absent. Read-only can't reach /connections
          in nav either, so this block simply does not render for Office /
          Accounts / Read-only. */}
      {canManage && (
      <div style={{marginTop:20, paddingTop:16, borderTop:"1px solid var(--ach-row-divider)"}} data-testid="invoicing-settings">
        <div style={{fontSize:11, textTransform:"uppercase", letterSpacing:0.6, color:"var(--ach-text-2)", marginBottom:12}}>Invoicing</div>

        {/* Split charge types */}
        <div className="ach-field" style={{marginBottom:12}}>
          <label style={{display:"flex", alignItems:"center", gap:10, cursor:"pointer"}}>
            <input type="checkbox"
                   checked={!!cfg.split_charge_types}
                   onChange={e => { setCfg({...cfg, split_charge_types: e.target.checked}); setDirty(true); }}
                   disabled={busy === "save"}
                   data-testid="split-charge-types-toggle" />
            <span style={{fontSize:13, fontWeight:500}}>Split transport and accessories onto their own Xero lines</span>
          </label>
          <div className="helper" style={{marginLeft:26}}>
            Off (recommended for the first run): every hire posts as a single line — crane hire, transport
            and any extras rolled together. On: each type gets its own line on the Xero invoice with its
            own account code, so you can see the split in Xero at a glance.
          </div>
        </div>

        {/* Reveal per-type codes and entity-totals inclusion when the split is ON */}
        {cfg.split_charge_types && (
          <div style={{marginLeft:26, padding:12, background:"var(--ach-page-bg)", borderRadius:6, marginBottom:16}} data-testid="split-charge-details">
            <div style={{fontSize:12, fontWeight:600, marginBottom:8}}>Chart-of-account codes for each type</div>
            <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:10}}>
              {["crane","transport","accessory","other"].map(t => (
                <div key={t} className="ach-field">
                  <label style={{textTransform:"capitalize"}}>{t}</label>
                  <input value={(cfg.account_codes || {})[t] || ""}
                         onChange={e => setCodeFor(t, e.target.value)}
                         disabled={busy === "save"}
                         data-testid={`account-code-${t}`}
                         placeholder={t === "crane" ? "200" : t === "transport" ? "224" : t === "accessory" ? "202" : "200"} />
                </div>
              ))}
            </div>
            <div style={{fontSize:12, fontWeight:600, margin:"14px 0 8px"}}>Include in entity totals</div>
            <div className="helper" style={{marginBottom:8}}>
              Entity totals (the cross-company summary at month-end close) will only add up the types you tick here.
              Todd&apos;s rule: crane hire only. The others should stay off unless you specifically want them counted.
            </div>
            <div style={{display:"flex", gap:16, flexWrap:"wrap"}}>
              {["crane","transport","accessory","other"].map(t => (
                <label key={t} style={{display:"flex", alignItems:"center", gap:6, cursor:"pointer", fontSize:13, textTransform:"capitalize"}}>
                  <input type="checkbox"
                         checked={!!(cfg.entity_totals_include || {})[t]}
                         onChange={e => setIncludeFor(t, e.target.checked)}
                         disabled={busy === "save"}
                         data-testid={`entity-include-${t}`} />
                  {t}
                </label>
              ))}
            </div>
          </div>
        )}

        {/* One invoice per machine (inverted from consolidate_invoices) */}
        <div className="ach-field">
          <label style={{display:"flex", alignItems:"center", gap:10, cursor:"pointer"}}>
            <input type="checkbox"
                   checked={perMachineUI}
                   onChange={e => setPerMachine(e.target.checked)}
                   disabled={busy === "save"}
                   data-testid="per-machine-toggle" />
            <span style={{fontSize:13, fontWeight:500}}>One invoice per machine</span>
          </label>
          <div className="helper" style={{marginLeft:26}}>
            Each crane gets its own invoice in Xero, with its own PO number. If a customer has five hires
            with five different POs, that becomes five invoices. Off means today&apos;s behaviour — one
            invoice per customer per month, all their hires rolled together. You can override this per
            customer on the customer&apos;s edit page.
          </div>
        </div>
      </div>
      )}

      {canManage && dirty && (
        <div style={{marginTop:12, display:"flex", gap:8}}>
          <button className="ach-btn ach-btn--primary" onClick={save} disabled={busy === "save"} data-testid="btn-save-xero-integration">
            {busy === "save" ? "Saving…" : "Save"}
          </button>
          <button className="ach-btn ach-btn--ghost" onClick={()=>{ setDirty(false); api.get("/settings/xero-integration").then(r => setCfg(r.data)); }} disabled={busy === "save"}>Cancel</button>
        </div>
      )}

      {cfg.contact_sync_enabled && canManage && (
        <div style={{marginTop:16, padding:12, background:"var(--ach-page-bg)", borderRadius:8}}>
          <div style={{fontSize:13, fontWeight:600, marginBottom:6}}>Pull contacts from Xero → ACH</div>
          <div style={{fontSize:12, color:"var(--ach-text-2)", marginBottom:10}}>
            One-shot manual sync — reads every non-archived Xero contact, creates any that don&apos;t exist in ACH, updates existing ones by name match. Safe to run again; no duplicates.
          </div>
          <button className="ach-btn ach-btn--primary" onClick={runSync} disabled={busy === "sync"} data-testid="btn-sync-xero-contacts">
            {busy === "sync" ? "Syncing…" : "Sync contacts now"}
          </button>
        </div>
      )}

      {syncResult && (
        <div style={{marginTop:10, padding:10, background:"var(--ach-green-tint)", borderLeft:"3px solid var(--ach-green)", fontSize:13}} data-testid="xero-sync-result">
          <strong>Sync result:</strong> {syncResult.created} new, {syncResult.updated} linked/updated, {syncResult.skipped_count} skipped.
        </div>
      )}

      {flash && (
        <div style={{marginTop:10, padding:8, background: flash.tone === "green" ? "var(--ach-green-tint)" : "var(--ach-red-tint)", color: flash.tone === "green" ? "var(--ach-green)" : "var(--ach-red)", borderRadius:6, fontSize:12}}>
          {flash.text}
        </div>
      )}
    </div>
  );
}

// ---------- Resend domain-check modal ----------
function ResendDomainCheck({ onClose }) {
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    api.post("/connections/resend/domain-check")
      .then(r => setResult(r.data))
      .catch(e => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setBusy(false));
  }, []);

  const tone = result?.status === "verified" ? "green"
    : result?.status === "pending" ? "blue"
    : result?.status === "not_found" || result?.status === "failed" ? "red"
    : "neutral";
  const bg = tone === "green" ? "var(--ach-green-tint)"
    : tone === "red" ? "var(--ach-red-tint)"
    : tone === "blue" ? "var(--ach-blue-tint)"
    : "var(--ach-page-bg)";
  const color = tone === "green" ? "var(--ach-green)"
    : tone === "red" ? "var(--ach-red)"
    : tone === "blue" ? "var(--ach-blue)"
    : "var(--ach-text-1)";

  return (
    <div className="ach-modal-backdrop" data-testid="resend-domain-check-dialog" onClick={onClose}>
      <div className="ach-modal" style={{maxWidth: 520}} onClick={(e)=>e.stopPropagation()}>
        <div className="ach-modal__header">
          <h2 style={{margin:0, fontSize:16, fontWeight:600}}>Is your sending domain verified in Resend?</h2>
          <button className="ach-btn ach-btn--grey" onClick={onClose} style={{padding:"6px 10px"}} data-testid="resend-domain-check-close">Close</button>
        </div>
        <div className="ach-modal__body">
          {busy && <div style={{fontSize:13, color:"var(--ach-text-2)"}}>Asking Resend…</div>}
          {err && (
            <div style={{color:"var(--ach-red)", background:"var(--ach-red-tint)", padding:10, borderRadius:8, fontSize:13}} data-testid="resend-domain-check-error">
              {err}
            </div>
          )}
          {result && (
            <div>
              <div style={{padding:12, borderRadius:8, background: bg, color, fontSize:13, lineHeight:1.5}} data-testid="resend-domain-check-result">
                <div style={{fontWeight:600, marginBottom:4, textTransform:"uppercase", fontSize:11, letterSpacing:0.6}}>{result.status.replace("_"," ")}</div>
                {result.message}
              </div>
              <div style={{marginTop:12, fontSize:12, color:"var(--ach-text-2)"}}>
                Manage domains and DNS records in{" "}
                <a href="https://resend.com/domains" target="_blank" rel="noreferrer" style={{color:"var(--ach-red)"}}>Resend → Domains ↗</a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ConnectorCard({ item, onChanged, canManage }) {
  const { meta, state } = item;
  const [busy, setBusy] = useState("");
  const [dialog, setDialog] = useState(false);
  const [events, setEvents] = useState(null);
  const [err, setErr] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [resendCheck, setResendCheck] = useState(false);

  const status = STATUS_MAP[state.status] || STATUS_MAP["not-connected"];
  const isXeroOAuth = meta.auth_kind === "oauth2-platform";

  const doCall = async (action) => {
    setConfirm(null);
    setBusy(action); setErr("");
    try {
      await api.post(`/connections/${meta.key}/${action}`);
      await onChanged();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(""); }
  };

  const call = (action, confirmMsg) => {
    if (confirmMsg) {
      setConfirm({ title: `${meta.name}`, body: confirmMsg, onGo: () => doCall(action) });
      return;
    }
    doCall(action);
  };

  const loadEvents = async () => {
    if (events !== null) { setEvents(null); return; }
    const r = await api.get(`/connections/${meta.key}/events?limit=20`);
    setEvents(r.data.events);
  };

  return (
    <div className="ach-card" data-testid={`connector-${meta.key}`}>
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:12}}>
        <div style={{flex:1}}>
          <div style={{display:"flex", alignItems:"center", gap:10, flexWrap:"wrap"}}>
            <h2 className="ach-card__title" style={{margin:0}}>{meta.name}</h2>
            <StatusPill tone={status.tone} testid={`status-${meta.key}`}>{status.label}</StatusPill>
            {state.live_mode && <StatusPill tone="maroon">SWITCHED ON — LIVE</StatusPill>}
          </div>
          <div style={{fontSize:12, color:"var(--ach-text-2)", marginTop:6}}>{meta.description}</div>
        </div>
      </div>

      {state.status === "connected" && (
        <div style={{background:"var(--ach-page-bg)", padding:12, borderRadius:8, marginTop:12, fontSize:12, color:"var(--ach-text-2)"}}>
          <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:8}}>
            {Object.entries(state.credentials_masked || {}).map(([k, v]) => (
              <div key={k}><strong style={{color:"var(--ach-charcoal)"}}>{k.replace(/_/g, " ")}:</strong> {v}</div>
            ))}
            {Object.entries(state.config || {}).filter(([, v]) => v).map(([k, v]) => (
              <div key={k}><strong style={{color:"var(--ach-charcoal)"}}>{k.replace(/_/g, " ")}:</strong> {v}</div>
            ))}
          </div>
          <div style={{marginTop:8}}>Connected by {state.connected_by_name} · {formatTs(state.connected_at)} · last worked {formatTs(state.last_worked_at)}</div>
          {state.live_mode && state.switched_on_at && (
            <div>Switched on by {state.switched_on_by_name} · {formatTs(state.switched_on_at)}</div>
          )}
        </div>
      )}

      {state.status === "problem" && state.last_error && (
        <div style={{color:"var(--ach-red)", background:"var(--ach-red-tint)", padding:10, borderRadius:8, marginTop:12, fontSize:12}}>
          <strong>What went wrong:</strong> {state.last_error}
        </div>
      )}

      {err && (
        <div style={{color:"var(--ach-red)", background:"var(--ach-red-tint)", padding:8, borderRadius:6, marginTop:12, fontSize:12}} data-testid={`err-${meta.key}`}>{err}</div>
      )}

      {meta.post_connect_note && state.status === "connected" && !state.live_mode && (
        <div style={{fontSize:12, color:"var(--ach-text-2)", marginTop:12, fontStyle:"italic"}}>{meta.post_connect_note}</div>
      )}

      <div style={{display:"flex", gap:8, marginTop:14, flexWrap:"wrap", alignItems:"center"}}>
        {meta.coming_soon ? (
          <span style={{fontSize:12, color:"var(--ach-text-2)"}}>This connection isn&apos;t available yet.</span>
        ) : (
          <>
            {state.status !== "connected" && canManage && (
              isXeroOAuth ? (
                <XeroConnectButton onErr={setErr} />
              ) : (
                <button className="ach-btn ach-btn--primary" onClick={()=>setDialog(true)} data-testid={`btn-connect-${meta.key}`}>
                  {state.status === "problem" ? "Reconnect →" : "Connect →"}
                </button>
              )
            )}
            {state.status === "connected" && !isXeroOAuth && (
              <button className="ach-btn ach-btn--ghost" onClick={()=>call("test")} disabled={busy === "test"} data-testid={`btn-test-${meta.key}`}>
                {busy === "test" ? "Testing…" : "Test connection"}
              </button>
            )}
            {state.status === "connected" && meta.key === "resend" && (
              <button className="ach-btn ach-btn--ghost" onClick={()=>setResendCheck(true)} data-testid="btn-resend-domain-check">
                Check domain →
              </button>
            )}
            {state.status === "connected" && (
              <>
                {canManage && !state.live_mode && (
                  <button className="ach-btn ach-btn--primary" data-testid={`btn-switch-on-${meta.key}`}
                    onClick={()=>call("switch-on", `Switch on ${meta.name}?\n\nFrom now on the system will do the real thing (create real invoices / send real emails / run real checks) instead of just logging what it would have done.\n\nYou can switch it off any time.`)}
                    disabled={busy === "switch-on"}>
                    {busy === "switch-on" ? "Working…" : "Switch on →"}
                  </button>
                )}
                {canManage && state.live_mode && (
                  <button className="ach-btn ach-btn--grey" data-testid={`btn-switch-off-${meta.key}`}
                    onClick={()=>call("switch-off", `Switch off ${meta.name}?\n\nGoing back to shadow mode — everything will be logged again but no real actions will happen.`)}
                    disabled={busy === "switch-off"}>
                    {busy === "switch-off" ? "Working…" : "Switch off"}
                  </button>
                )}
                {canManage && (
                  <button className="ach-btn ach-btn--grey" data-testid={`btn-disconnect-${meta.key}`}
                    onClick={()=>call("disconnect", `Disconnect ${meta.name}?\n\nCredentials will be wiped. You'll need to re-enter them to reconnect.`)}
                    disabled={busy === "disconnect"}>
                    Disconnect
                  </button>
                )}
              </>
            )}
            <button className="ach-btn ach-btn--grey" onClick={loadEvents} data-testid={`btn-events-${meta.key}`}>
              {events !== null ? "Hide history" : "Show history"}
            </button>
          </>
        )}
      </div>

      {isXeroOAuth && state.status === "connected" && (
        <XeroIntegrationSettings canManage={canManage} />
      )}

      {events !== null && (
        <div style={{marginTop:14, borderTop:"1px solid var(--ach-row-divider)", paddingTop:12}} data-testid={`events-${meta.key}`}>
          <div style={{fontSize:11, textTransform:"uppercase", letterSpacing:0.6, color:"var(--ach-text-2)", marginBottom:8}}>History</div>
          {events.length === 0 ? (
            <div style={{fontSize:12, color:"var(--ach-text-2)"}}>Nothing yet.</div>
          ) : events.map(e => (
            <div key={e.id} style={{display:"flex", gap:12, padding:"6px 0", borderBottom:"1px solid var(--ach-row-divider)", fontSize:12}}>
              <StatusPill tone={e.kind.includes("failed") || e.kind === "disconnected" ? "red" : e.kind === "connected" || e.kind === "switched-on" ? "green" : "neutral"}>
                {EVENT_LABEL[e.kind] || e.kind.toUpperCase()}
              </StatusPill>
              <span style={{flex:1}}>{e.actor_name || "System"} — {e.detail}</span>
              <span style={{color:"var(--ach-text-2)"}}>{formatTs(e.at)}</span>
            </div>
          ))}
        </div>
      )}

      {dialog && <ConnectDialog meta={meta} initial={state} onClose={()=>setDialog(false)} onSaved={onChanged} />}
      {resendCheck && <ResendDomainCheck onClose={()=>setResendCheck(false)} />}

      {confirm && (
        <div className="ach-modal-backdrop" data-testid={`confirm-${meta.key}-dialog`} onClick={()=>setConfirm(null)}>
          <div className="ach-modal" style={{maxWidth: 480}} onClick={(e)=>e.stopPropagation()}>
            <div className="ach-modal__header">
              <h2 style={{margin:0,fontSize:16,fontWeight:600}}>{confirm.title}</h2>
            </div>
            <div className="ach-modal__body">
              <p style={{margin:0, fontSize:13, color:"var(--ach-text-1)", lineHeight:1.5, whiteSpace:"pre-wrap"}}>{confirm.body}</p>
            </div>
            <div className="ach-modal__footer" style={{display:"flex", gap:8, justifyContent:"flex-end"}}>
              <button className="ach-btn ach-btn--grey" onClick={()=>setConfirm(null)} data-testid={`confirm-${meta.key}-cancel`}>Cancel</button>
              <button className="ach-btn ach-btn--primary" onClick={confirm.onGo} data-testid={`confirm-${meta.key}-go`}>Yes, do it</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Connections() {
  const [items, setItems] = useState([]);
  const [flash, setFlash] = useState(null); // {tone, text}
  const { user } = useAuth();
  const location = useLocation();
  const nav = useNavigate();
  const canManage = user?.role === "Director";

  const load = () => api.get("/connections").then(r => setItems(r.data.connections));
  useEffect(() => { load(); }, []);

  // Pick up the ?xero=ok|failed&msg=... flash from the OAuth callback redirect
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const xero = params.get("xero");
    if (!xero) return;
    const msg = params.get("msg") || (xero === "ok" ? "Connected to Xero." : "Xero connection didn't complete.");
    setFlash({ tone: xero === "ok" ? "green" : "red", text: msg });
    // Clear the query so a refresh doesn't repeat the flash.
    nav("/connections", { replace: true });
    // Reload so the freshly-connected Xero card shows the new state.
    load();
    const t = setTimeout(() => setFlash(null), 8000);
    return () => clearTimeout(t);
  }, [location.search, nav]);

  return (
    <div data-testid="connections-page">
      <PageHeader
        title="Connections"
        subtitle="Hook this system up to your own software — one connection at a time"
      />

      {flash && (
        <div data-testid="xero-flash"
          style={{
            marginBottom:16, padding:12, borderRadius:8, fontSize:13, fontWeight:500,
            background: flash.tone === "green" ? "var(--ach-green-tint)" : "var(--ach-red-tint)",
            color: flash.tone === "green" ? "var(--ach-green)" : "var(--ach-red)",
            border: `1px solid ${flash.tone === "green" ? "var(--ach-green)" : "var(--ach-red)"}`,
          }}>
          {flash.text}
        </div>
      )}

      <div className="ach-card" style={{marginBottom: 16, background:"var(--ach-blue-tint)", borderColor: "var(--ach-blue)"}}>
        <div style={{fontSize:13, color:"var(--ach-blue)"}}>
          <strong>How this works — two safe steps:</strong> First, <strong>connect</strong> (for Xero, you&apos;ll be sent to Xero&apos;s own sign-in; for the others, paste the key from your software and we check it works). That&apos;s harmless — the system keeps logging what it would do. Then, when you&apos;re ready, <strong>switch on</strong>. From that moment the system does the real thing (creates the invoice, sends the email, etc.). You can switch it back off any time.
          {!canManage && <div style={{marginTop:8}}>You&apos;re signed in as <strong>{user?.role}</strong> — only a Director can connect or switch things on.</div>}
        </div>
      </div>

      <div style={{display:"grid", gap:16}}>
        {items.map(it => (
          <ConnectorCard key={it.meta.key} item={it} onChanged={load} canManage={canManage} />
        ))}
        <DocumentsCard canManage={canManage} />
      </div>
    </div>
  );
}

// ---------- Documents (quote / invoice PDF storage) ---------------------------
// Sep 2026. Todd's PDF button did nothing (turned out to be an auth issue on
// the FE, fixed alongside this). This card lets a Director pick where the
// PDFs land: local disk on the server today, or SharePoint in Todd's own
// tenant when his admin has run the 10-minute Entra setup.
function DocumentsCard({ canManage }) {
  const [cfg, setCfg]           = useState(null);
  const [backend, setBackend]   = useState("local");
  const [sp, setSp]             = useState({tenant_id:"", client_id:"", site_id:"", folder:"ACH-Hire", client_secret:""});
  const [busy, setBusy]         = useState(false);
  const [saveMsg, setSaveMsg]   = useState("");
  const [testResult, setTestResult] = useState(null);

  const load = () => api.get("/storage/config").then(r => {
    setCfg(r.data);
    setBackend(r.data.backend || "local");
    if (r.data.sharepoint) {
      setSp(s => ({ ...s,
        tenant_id: r.data.sharepoint.tenant_id || "",
        client_id: r.data.sharepoint.client_id || "",
        site_id:   r.data.sharepoint.site_id   || "",
        folder:    r.data.sharepoint.folder    || "ACH-Hire",
        // client_secret is deliberately never returned — we don't overwrite
        // the local state field. If the user wants to change it, they type
        // it; leaving it blank on save preserves the existing encrypted one.
      }));
    }
  }).catch(() => setCfg({backend:"local", configured:true}));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true); setSaveMsg(""); setTestResult(null);
    try {
      const payload = { backend };
      if (backend === "sharepoint") {
        Object.assign(payload, {
          tenant_id: sp.tenant_id.trim(),
          client_id: sp.client_id.trim(),
          site_id:   sp.site_id.trim(),
          folder:    sp.folder.trim() || "ACH-Hire",
        });
        if (sp.client_secret) payload.client_secret = sp.client_secret;
      }
      await api.put("/storage/config", payload);
      setSaveMsg("Saved. Storage settings updated.");
      setSp(s => ({ ...s, client_secret: "" }));  // never keep the secret in local state after save
      await load();
    } catch (e) {
      setSaveMsg(`Couldn't save: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const test = async () => {
    setBusy(true); setTestResult(null);
    try {
      const r = await api.post("/storage/test");
      setTestResult(r.data);
    } catch (e) {
      setTestResult({ok: false, message: e.response?.data?.detail || e.message});
    } finally { setBusy(false); }
  };

  if (!cfg) return null;
  const isSp = backend === "sharepoint";

  return (
    <div className="ach-card" data-testid="documents-card">
      <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8}}>
        <h2 className="ach-card__title" style={{margin:0}}>Documents</h2>
        <div style={{fontSize:12, color: cfg.configured ? "var(--ach-green)" : "var(--ach-amber)"}}>
          {cfg.configured ? `Configured: ${cfg.backend === "local" ? "local disk" : "SharePoint"}` : "Not configured"}
        </div>
      </div>
      <p style={{fontSize:13, color:"var(--ach-text-1)", lineHeight:1.5, margin:"0 0 12px"}}>
        Where the quote and invoice PDFs get stored. Local disk is the day-one
        default and works out of the box on this server. SharePoint puts the
        files in Todd&apos;s own document library — same interface, just a
        different destination.
      </p>

      <div className="ach-form-grid" style={{marginBottom: 12}}>
        <div className="ach-field" style={{gridColumn:"1 / -1"}}>
          <label>Where to store files</label>
          <div style={{display:"flex", flexDirection:"column", gap:6, marginTop:4}}>
            <label style={{display:"flex", alignItems:"center", gap:8, fontSize:13, cursor:"pointer"}}>
              <input type="radio" name="doc-backend" value="local" checked={backend === "local"}
                     onChange={()=>setBackend("local")} disabled={!canManage}
                     style={{width:"auto", flex:"0 0 auto"}} data-testid="doc-backend-local" />
              On this server (local disk) — works today
            </label>
            <label style={{display:"flex", alignItems:"center", gap:8, fontSize:13, cursor:"pointer"}}>
              <input type="radio" name="doc-backend" value="sharepoint" checked={backend === "sharepoint"}
                     onChange={()=>setBackend("sharepoint")} disabled={!canManage}
                     style={{width:"auto", flex:"0 0 auto"}} data-testid="doc-backend-sharepoint" />
              SharePoint (Microsoft 365) — files land in your own library
            </label>
          </div>
        </div>

        {isSp && (
          <>
            <div className="ach-field"><label>Tenant ID</label>
              <input value={sp.tenant_id} onChange={(e)=>setSp({...sp, tenant_id:e.target.value})} disabled={!canManage} data-testid="sp-tenant" placeholder="e.g. 11111111-2222-…" /></div>
            <div className="ach-field"><label>Client ID (application ID)</label>
              <input value={sp.client_id} onChange={(e)=>setSp({...sp, client_id:e.target.value})} disabled={!canManage} data-testid="sp-client" /></div>
            <div className="ach-field" style={{gridColumn:"1 / -1"}}><label>Client secret {cfg.sharepoint?.has_client_secret ? "(leave blank to keep the saved one)" : ""}</label>
              <input type="password" value={sp.client_secret} onChange={(e)=>setSp({...sp, client_secret:e.target.value})} disabled={!canManage} data-testid="sp-secret" /></div>
            <div className="ach-field"><label>SharePoint site ID</label>
              <input value={sp.site_id} onChange={(e)=>setSp({...sp, site_id:e.target.value})} disabled={!canManage} data-testid="sp-site" placeholder="hostname,site-guid,web-guid" /></div>
            <div className="ach-field"><label>Folder inside the library</label>
              <input value={sp.folder} onChange={(e)=>setSp({...sp, folder:e.target.value})} disabled={!canManage} data-testid="sp-folder" placeholder="ACH-Hire" /></div>
            <div className="ach-field" style={{gridColumn:"1 / -1"}}>
              <div className="helper" style={{fontSize:12, color:"var(--ach-text-2)", lineHeight:1.5}}>
                The setup sheet for your Entra admin lists exactly which permission to grant (<code>Sites.Selected</code>, not <code>Files.ReadWrite.All</code>) and how to scope it to just one library. Ask your Kindra contact for the sheet.
              </div>
            </div>
          </>
        )}
      </div>

      <div style={{display:"flex", gap:8, marginBottom: 8}}>
        <button className="ach-btn ach-btn--primary" onClick={save} disabled={!canManage || busy} data-testid="doc-save">
          {busy ? "Saving…" : "Save"}
        </button>
        <button className="ach-btn ach-btn--grey" onClick={test} disabled={!canManage || busy} data-testid="doc-test">
          {busy ? "Testing…" : "Test — write, read, delete"}
        </button>
      </div>
      {saveMsg && <div style={{fontSize:12, color:"var(--ach-text-1)", marginBottom: 6}} data-testid="doc-save-msg">{saveMsg}</div>}
      {testResult && (
        <div data-testid="doc-test-result" style={{
          padding: 10, borderRadius: 6, fontSize: 13, lineHeight: 1.5,
          background: testResult.ok ? "var(--ach-green-tint)" : "var(--ach-red-tint)",
          color:      testResult.ok ? "var(--ach-green)"      : "var(--ach-red)",
          border:  `1px solid ${testResult.ok ? "var(--ach-green)" : "var(--ach-red)"}`,
        }}>
          <div style={{fontWeight:600, marginBottom:4}}>{testResult.ok ? "Test passed" : "Test failed"}</div>
          <div>{testResult.message}</div>
          {testResult.ok && (
            <div style={{fontSize:12, marginTop:4, opacity:0.85}}>
              Wrote {testResult.wrote} bytes, read {testResult.read} back, deleted the test file.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
